/**
 * Handles localStorage persistence for stories page state.
 *
 * Every filter the user sets is remembered until they clear it, so reopening or
 * refreshing the page lands them back on the same view.
 */

import {
  DEFAULT_DATE_FILTER,
  DOCUMENT_TYPES,
  type DateFilter,
  type DocumentType,
} from "./stories.filters";

const STORAGE_KEYS = {
  STATUS: "sd-stories-page-status",
  SORT_BY: "sd-stories-sort-by",
  SORT_ORDER: "sd-stories-sort-order",
  LAYOUT_VIEW: "sd-stories-layout-view",
  TYPES: "sd-stories-types",
  OWNER: "sd-stories-owner",
  LOCATION: "sd-stories-location",
  DATE: "sd-stories-date",
} as const;

const DEFAULT_STATUS = ["Live", "Draft"] as const;
const DEFAULT_SORT_BY = "updatedAt";
const DEFAULT_SORT_ORDER = "descending";
const DEFAULT_LAYOUT_VIEW = "row";

/**
 * Reads and JSON-parses a key, falling back when the slot is missing or holds
 * something the current build no longer understands.
 */
function readJSON<T>(
  key: string,
  fallback: T,
  isValid: (v: unknown) => boolean,
): T {
  if (typeof window === "undefined") return fallback;
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return fallback;
    const parsed: unknown = JSON.parse(stored);
    return isValid(parsed) ? (parsed as T) : fallback;
  } catch (error) {
    console.error(`Error parsing ${key} from localStorage:`, error);
    return fallback;
  }
}

function writeJSON(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
  }
}

function readString(key: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  return localStorage.getItem(key) || fallback;
}

function writeString(key: string, value: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
  }
}

/* ------------------------------------------------------------------ status */

export function getStatusFromStorage(): string[] {
  return readJSON<string[]>(STORAGE_KEYS.STATUS, [...DEFAULT_STATUS], (v) =>
    Array.isArray(v),
  );
}

export function saveStatusToStorage(status: string[]): void {
  writeJSON(STORAGE_KEYS.STATUS, status);
}

/* -------------------------------------------------------------------- sort */

export function getSortByFromStorage(): string {
  return readString(STORAGE_KEYS.SORT_BY, DEFAULT_SORT_BY);
}

export function saveSortByToStorage(sortBy: string): void {
  writeString(STORAGE_KEYS.SORT_BY, sortBy);
}

export function getSortOrderFromStorage(): string {
  return readString(STORAGE_KEYS.SORT_ORDER, DEFAULT_SORT_ORDER);
}

export function saveSortOrderToStorage(sortOrder: string): void {
  writeString(STORAGE_KEYS.SORT_ORDER, sortOrder);
}

/* ------------------------------------------------------------- layout view */

export function getLayoutViewFromStorage(): "row" | "grid" {
  const stored = readString(STORAGE_KEYS.LAYOUT_VIEW, DEFAULT_LAYOUT_VIEW);
  return stored === "row" || stored === "grid" ? stored : DEFAULT_LAYOUT_VIEW;
}

export function saveLayoutViewToStorage(layoutView: "row" | "grid"): void {
  writeString(STORAGE_KEYS.LAYOUT_VIEW, layoutView);
}

/* -------------------------------------------------------------- type filter */

/** Default is every type, i.e. the unfiltered list the user had before. */
export function getTypesFromStorage(): DocumentType[] {
  return readJSON<DocumentType[]>(
    STORAGE_KEYS.TYPES,
    [...DOCUMENT_TYPES],
    (v) =>
      Array.isArray(v) &&
      v.every((t) =>
        (DOCUMENT_TYPES as readonly string[]).includes(t as string),
      ),
  );
}

export function saveTypesToStorage(types: DocumentType[]): void {
  writeJSON(STORAGE_KEYS.TYPES, types);
}

/* ------------------------------------------------------------- owner filter */

export function getOwnerFromStorage(): string {
  return readString(STORAGE_KEYS.OWNER, "");
}

export function saveOwnerToStorage(owner: string): void {
  writeString(STORAGE_KEYS.OWNER, owner);
}

/* ---------------------------------------------------------- location filter */

export function getLocationFromStorage(): string[] {
  return readJSON<string[]>(STORAGE_KEYS.LOCATION, [], (v) => Array.isArray(v));
}

export function saveLocationToStorage(folderIds: string[]): void {
  writeJSON(STORAGE_KEYS.LOCATION, folderIds);
}

/* -------------------------------------------------------------- date filter */

export function getDateFilterFromStorage(): DateFilter {
  return readJSON<DateFilter>(
    STORAGE_KEYS.DATE,
    DEFAULT_DATE_FILTER,
    (v) => typeof v === "object" && v !== null && "field" in v && "preset" in v,
  );
}

export function saveDateFilterToStorage(dateFilter: DateFilter): void {
  writeJSON(STORAGE_KEYS.DATE, dateFilter);
}
